import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import {
  Briefcase, MapPin, Building2, Users, FileText,
  DollarSign, Clock, Settings, ArrowRight, Loader2,
  CheckCircle2, Home, Bus, GraduationCap
} from 'lucide-react';

interface Workflow {
  id: string;
  name: string;
}

export default function CreateCampaign() {
  const { activeUserId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);

  const [formData, setFormData] = useState({
    title: '',
    default_job_title: '',
    description: '',
    address: '',
    target_locations: '',
    salary_min: '',
    salary_max: '',
    salary_currency: 'AED',
    working_hours: '',
    accommodation: false,
    transportation: false,
    number_of_vacancies: '',
    required_education: '',
    min_age: '',
    max_age: '',
    preferred_gender: 'Any',
    workflow_id: ''
  });

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const { data, error } = await supabase.from('workflows').select('id, name');
      if (error) throw error;
      if (data) setWorkflows(data);
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeUserId) return;

    setLoading(true);
    try {
      const { data: campaign, error } = await supabase.from('campaigns').insert({
        recruiter_id: activeUserId,
        title: formData.title,
        default_job_title: formData.default_job_title,
        description: formData.description,
        address: formData.address,
        target_locations: formData.target_locations ? formData.target_locations.split(',').map(s => s.trim()) : [],
        salary_min: parseInt(formData.salary_min) || null,
        salary_max: parseInt(formData.salary_max) || null,
        salary_currency: formData.salary_currency,
        working_hours: formData.working_hours,
        accommodation: formData.accommodation,
        transportation: formData.transportation,
        number_of_vacancies: parseInt(formData.number_of_vacancies) || 1,
        required_education: formData.required_education,
        min_age: parseInt(formData.min_age) || null,
        max_age: parseInt(formData.max_age) || null,
        preferred_gender: formData.preferred_gender,
        workflow_id: formData.workflow_id || null,
        status: 'draft'
      }).select().single();

      if (error) throw error;

      navigate(`/app/campaigns/${campaign.id}`);
    } catch (err: any) {
      console.error('Error creating campaign:', err);
      alert('Failed to create campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const baseInputClass = "w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-2.5 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 focus:bg-white transition-all placeholder:text-slate-400 text-[13px] font-medium";
  const labelClass = "block text-[12px] font-semibold text-slate-500 mb-1.5 mt-4 flex items-center gap-2";

  return (
    <div className="max-w-5xl mx-auto pb-12 animate-in fade-in" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Create Campaign</h1>
          <p className="text-slate-400 mt-1 text-[15px]">Define requirements and launch a hiring drive</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* General Information Card */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
          <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Briefcase size={24} /></div>
            <h2 className="text-lg font-black text-slate-900 tracking-tight">General Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className={labelClass}><FileText size={16} /> Campaign Title</label>
              <input required name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Dubai Marina Cleaners Drive 2026" className={baseInputClass} />
            </div>

            <div>
              <label className={labelClass}><Briefcase size={16} /> Default Job Title</label>
              <input required name="default_job_title" value={formData.default_job_title} onChange={handleChange} placeholder="e.g. Cleaner" className={baseInputClass} />
            </div>

            <div>
              <label className={labelClass}><Users size={16} /> Number of Vacancies</label>
              <input type="number" required name="number_of_vacancies" value={formData.number_of_vacancies} onChange={handleChange} placeholder="e.g. 50" className={baseInputClass} />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}><FileText size={16} /> Description</label>
              <textarea required name="description" value={formData.description} onChange={handleChange} rows={4} placeholder="Describe the campaign, roles, and benefits..." className={`${baseInputClass} resize-none`} />
            </div>
          </div>
        </div>

        {/* Location & Workflow Card */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
          <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><MapPin size={24} /></div>
            <h2 className="text-lg font-black text-slate-900 tracking-tight">Location & Pipeline</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className={labelClass}><Building2 size={16} /> Interview / Work Address</label>
              <input required name="address" value={formData.address} onChange={handleChange} placeholder="Full address for candidates to arrive at" className={baseInputClass} />
            </div>

            <div>
              <label className={labelClass}><MapPin size={16} /> Target Candidate Locations</label>
              <input name="target_locations" value={formData.target_locations} onChange={handleChange} placeholder="e.g. India, Nepal, Philippines" className={baseInputClass} />
            </div>

            <div>
              <label className={labelClass}><Settings size={16} /> Hiring Workflow</label>
              <select required name="workflow_id" value={formData.workflow_id} onChange={handleChange} className={baseInputClass}>
                <option value="">Select a workflow pipeline</option>
                {workflows.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Compensation & Benefits */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
          <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
            <div className="p-2 bg-green-50 text-green-600 rounded-lg"><DollarSign size={24} /></div>
            <h2 className="text-lg font-black text-slate-900 tracking-tight">Compensation & Benefits</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={labelClass}>Min Salary</label>
              <input type="number" name="salary_min" value={formData.salary_min} onChange={handleChange} placeholder="e.g. 1500" className={baseInputClass} />
            </div>
            <div>
              <label className={labelClass}>Max Salary</label>
              <input type="number" name="salary_max" value={formData.salary_max} onChange={handleChange} placeholder="e.g. 2000" className={baseInputClass} />
            </div>
            <div>
              <label className={labelClass}>Currency</label>
              <select name="salary_currency" value={formData.salary_currency} onChange={handleChange} className={baseInputClass}>
                <option value="AED">AED</option>
                <option value="SAR">SAR</option>
                <option value="QAR">QAR</option>
                <option value="INR">INR</option>
              </select>
            </div>

            <div className="md:col-span-3">
              <label className={labelClass}><Clock size={16} /> Working Hours</label>
              <input name="working_hours" value={formData.working_hours} onChange={handleChange} placeholder="e.g. 9 Hours / 6 Days" className={baseInputClass} />
            </div>

            <div className="md:col-span-3 flex gap-8 p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" name="accommodation" checked={formData.accommodation} onChange={handleChange} className="w-5 h-5 rounded border-gray-300 text-jobisa-navy focus:ring-jobisa-yellow" />
                <span className="flex items-center gap-2 font-medium text-gray-700"><Home size={18} className="text-gray-400" /> Accommodation Provided</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" name="transportation" checked={formData.transportation} onChange={handleChange} className="w-5 h-5 rounded border-gray-300 text-jobisa-navy focus:ring-jobisa-yellow" />
                <span className="flex items-center gap-2 font-medium text-gray-700"><Bus size={18} className="text-gray-400" /> Transportation Provided</span>
              </label>
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
          <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg"><CheckCircle2 size={24} /></div>
            <h2 className="text-lg font-black text-slate-900 tracking-tight">Candidate Requirements</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <label className={labelClass}><GraduationCap size={16} /> Minimum Education</label>
              <select name="required_education" value={formData.required_education} onChange={handleChange} className={baseInputClass}>
                <option value="None">No Requirement</option>
                <option value="Primary">Primary School</option>
                <option value="High School">High School</option>
                <option value="Diploma">Diploma</option>
                <option value="Degree">Degree</option>
              </select>
            </div>

            <div className="col-span-2 grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Min Age</label>
                <input type="number" name="min_age" value={formData.min_age} onChange={handleChange} placeholder="21" className={baseInputClass} />
              </div>
              <div>
                <label className={labelClass}>Max Age</label>
                <input type="number" name="max_age" value={formData.max_age} onChange={handleChange} placeholder="45" className={baseInputClass} />
              </div>
              <div>
                <label className={labelClass}>Gender</label>
                <select name="preferred_gender" value={formData.preferred_gender} onChange={handleChange} className={baseInputClass}>
                  <option value="Any">Any</option>
                  <option value="Male">Male Only</option>
                  <option value="Female">Female Only</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/app/campaigns')}
            className="px-6 py-4 text-gray-500 font-bold hover:text-gray-800 transition-colors mr-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-slate-950 text-white px-8 py-3 rounded-xl text-[14px] font-semibold hover:bg-slate-800 hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:hover:translate-y-0"
          >
            {loading ? <Loader2 size={24} className="animate-spin" /> : <><Briefcase fill="currentColor" size={20} /> Launch Campaign <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" /></>}
          </button>
        </div>

      </form>
    </div>
  );
}
